import { html, render } from 'lit';
import { htmlDecode } from '../../utils/escape.utils';
import { ColumnDetails, ColumnType } from '../types/common';
import { getLabelForEnumValue } from './columns';
import { dateFormatter } from '@nirvana/shared/time';
import { longToNumber } from '@nirvana/shared/basic-types/long';
import { SelectOption } from '../../ui/gzSelect/types';
import { InlineRowActionsWebComponent } from '../uiElements/inlineRowActions';
import { WEB_COMPONENT_GRID_CELL_EDIT, WEB_COMPONENT_INLINE_ROW_ACTIONS } from '../../webcomponents.constants';
import { EnumListItem } from '../../ui/gzEnumList/types';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { translateFn } from '../../utils/translator.helper';
import { dataGridStrings } from './gridStrings';
import { GzTooltip } from '../../ui/gzTooltip';
import { GridCellEditWebComponent } from '../uiElements/gridCellEdit';
import { isElementOverflowed, OverflowDirection } from '../../utils/htmlElement.utils';
import { ListItem } from '../../ui/gzSingleSelectList/types';
import { IconProperties } from '../../ui/gzIcon/types';

export function onGridCellMouseEnter(event: MouseEvent) {
  const item = event.target as Element;
  const label = item.querySelector(WEB_COMPONENT_GRID_CELL_EDIT)
    ? (item.querySelector(WEB_COMPONENT_GRID_CELL_EDIT) as GridCellEditWebComponent)
    : (item.querySelector('.cell-label') as HTMLElement);

  if (!label || !isElementOverflowed(label, OverflowDirection.HORIZONTAL)) {
    return;
  }

  const tooltipText = label instanceof GridCellEditWebComponent ? label.value : label.textContent;

  GzTooltip.show(tooltipText, event);
}

export function columnContentRenderer<Column extends number, ColumnKeys extends string>(
  root,
  rowData,
  columnDetails: ColumnDetails<Column, ColumnKeys>
) {
  render(
    html`
      <span
        @mouseenter="${e => onGridCellMouseEnter(e)}"
        @mousemove="${e => GzTooltip.align(e)}"
        @mouseleave="${GzTooltip.hide}"
        class="${columnDetails.editingDisabled ? '' : 'column-editable'} row-${rowData.index + 2}"
      >
        ${getCellRenderer.call(this, rowData, columnDetails)}
      </span>
    `,
    root
  );
}

export function getCellRenderer<Column extends number, ColumnKeys extends string>(
  rowData,
  columnDetails: ColumnDetails<Column, ColumnKeys>
) {
  const cellValue = rowData.item[columnDetails.key];

  if (isEmpty(cellValue) && columnDetails.editingDisabled) {
    return '-';
  }

  if (this.columnRenderers && this.columnRenderers[columnDetails.key]) {
    return this.columnRenderers[columnDetails.key](rowData, columnDetails);
  }

  if (columnDetails.type === ColumnType.DATE) {
    const timezone = this.dependencies.getTimezone();
    return gridDateTimeRenderer<Column, ColumnKeys>(rowData, columnDetails, timezone);
  }

  if (columnDetails.type === ColumnType.ENUM) {
    return enumRenderer(rowData, columnDetails);
  }

  if (columnDetails.type === ColumnType.EDITABLE_SELECT) {
    return selectRenderer(rowData, columnDetails);
  }

  if (columnDetails.type === ColumnType.MULTIPLE_VALUE) {
    return multipleValueRenderer(rowData, columnDetails);
  }

  return gridCellDefaultRenderer(rowData, columnDetails);
}

export function gridCellDefaultRenderer<Column extends number, ColumnKeys extends string>(
  rowData,
  columnDetails: ColumnDetails<Column, ColumnKeys>
) {
  let cellValue = rowData.item[columnDetails.key];
  cellValue = Array.isArray(cellValue) ? cellValue.join(', ') : cellValue;
  const copyContentMessage = columnDetails.canCopyContent ? translateFn('Copied') : undefined;

  if (columnDetails.editingDisabled) {
    return html` <gz-grid-cell-default-renderer
      .label="${cellValue}"
      .copyContentMessage="${copyContentMessage}"
      ?canCopyContent="${columnDetails.canCopyContent}"
      ?selected="${rowData.selected}"
    >
    </gz-grid-cell-default-renderer>`;
  } else {
    // htmlDecode will convert the number into a string so we need to remove it from number type column
    const editableValue = columnDetails.type === ColumnType.NUMBER ? cellValue : htmlDecode(cellValue);
    return html`<gz-grid-cell-edit
      .type="${columnDetails.type}"
      .value="${editableValue}"
      ?canCopyContent="${columnDetails.canCopyContent}"
      .copyContentMessage="${copyContentMessage}"
      id="${columnDetails.key}"
      ?selected="${rowData.selected}"
    ></gz-grid-cell-edit>`;
  }
}

export function enumRenderer(rowData, columnDetails) {
  const cellValue = rowData.item[columnDetails.key];

  if (isEmpty(cellValue)) {
    return '-';
  }

  if (columnDetails.editingDisabled || !Array.isArray(cellValue)) {
    const label = getLabelForEnumValue(columnDetails, cellValue);
    return defaultLabelRenderer(label);
  } else {
    const enumValues = columnDetails.values;
    const labels = cellValue.map(val => getLabelForEnumValue(columnDetails, val)).join(', ');
    const dropdownOptions =
      enumValues &&
      enumValues.map(enumValue => {
        // match cell values against all the column values, so we can get the selected items for the dropdown
        const matches = cellValue.filter(value => value === enumValue.value);
        const selected = !!matches.length;

        const entry: EnumListItem = {
          label: enumValue.label,
          value: enumValue.value,
          selected: selected,
        };

        return entry;
      });

    const copyContentMessage = columnDetails.canCopyContent ? translateFn('Copied') : undefined;
    const { confirmString, allItemsLabel } = dataGridStrings().enumColumnStrings;

    return html`<gz-grid-cell-edit
      .type="${columnDetails.type}"
      .value="${labels}"
      id="${columnDetails.key}"
      .filterOptions="${dropdownOptions}"
      allItemsLabel="${allItemsLabel}"
      confirmActionButtonString="${confirmString}"
      ?canCopyContent="${columnDetails.canCopyContent}"
      .copyContentMessage="${copyContentMessage}"
    ></gz-grid-cell-edit>`;
  }
}

export function selectRenderer(rowData, columnDetails) {
  const cellValue = rowData.item[columnDetails.key];
  if (isEmpty(cellValue)) {
    return '-';
  }
  if (columnDetails.editingDisabled) {
    return defaultLabelRenderer(getLabelForEnumValue(columnDetails, cellValue));
  } else {
    const selectValues = columnDetails.values;
    const dropdownOptions = selectValues?.map(
      selectValue => ({ label: selectValue.label, value: selectValue.value } as ListItem)
    );

    const copyContentMessage = columnDetails.canCopyContent ? translateFn('Copied') : undefined;

    return html`<gz-grid-cell-edit
      .type="${columnDetails.type}"
      .value="${cellValue}"
      id="${columnDetails.key}"
      .filterOptions="${dropdownOptions}"
      ?canCopyContent="${columnDetails.canCopyContent}"
      .copyContentMessage="${copyContentMessage}"
      ?selectWithDynamicSearch="${columnDetails.selectWithDynamicSearch}"
    ></gz-grid-cell-edit>`;
  }
}

export function defaultLabelRenderer(label: string) {
  return html`<span class="cell-label label">${htmlDecode(label)}</span>`;
}

export function gridDateTimeRenderer<Column extends number, ColumnKeys extends string>(
  rowData,
  columnDetails: ColumnDetails<Column, ColumnKeys>,
  timezone
) {
  const cellValue = rowData.item[columnDetails.key];

  if (isEmpty(cellValue)) {
    return '-';
  }

  const formattedDate = getFormatedDate(cellValue, timezone);
  return html`<span class="cell-label label">${htmlDecode(formattedDate)}</span>`;
}

export function getFormatedDate(value: { low: number; high: number; unsigned: boolean }, timezone: string): string {
  let timestamp;

  // Date is represented as a timestamp in milliseconds on the backend. This timestamp is int64,
  // encoded as a Long by protobuf (https://www.npmjs.com/package/long). On the browser side,
  // we can easily work with JS number because timestamps will fit in 53 bits. We don't want any
  // extra dependencies though, so convert the Long manually to number.
  try {
    timestamp = longToNumber(value);
  } catch (err) {
    throw new Error(`Unexpected DATE value: ${JSON.stringify(value, null, 2)}`);
  }

  return dateFormatter(timestamp, timezone);
}

export function getFormattedDateString(value: { low: number; high: number; unsigned: boolean }, timezone: string) {
  const formattedDate = getFormatedDate(value, timezone);

  return `<span class="cell-label label">${htmlDecode(formattedDate)}</span>`;
}

/**
 * Returns html node constructed from given html code
 *
 * This function is used to render custom html inside a grid
 *
 * DEPRECATED (see @deprecated below)
 *
 * @param {string} htmlCode
 * @returns {DocumentFragment}
 *
 * @deprecated Use html from "lit-html" instead, to render custom html. It's more powerful and it has protection against XSS attacks.
 */
export function gridCustomHtmlRenderer(htmlCode: string) {
  /**
   * https://vaadin.com/components/vaadin-grid/html-api/elements/Vaadin.GridColumnElement
   * We can use the render() function from 'lit-element' to return HTML content
   * see: https://glitch.com/edit/#!/vaadin-grid-renderer?path=app.js%3A2%3A0
   * render(
   * html`<span>${rowData.item.content}</span>`, root
   * );
   */
  return document.createRange().createContextualFragment(htmlDecode(htmlCode));
}

export function defaultHeaderRenderer(columnName: string): string {
  return `<span class="text-semibold cell-label cell-label--header">${columnName}</span>`;
}

export function rowInlineMenuColumnRenderer(root, rowData, actions: Array<SelectOption>) {
  let menuBtn = root.firstElementChild as InlineRowActionsWebComponent;
  if (!menuBtn) {
    menuBtn = window.document.createElement(WEB_COMPONENT_INLINE_ROW_ACTIONS) as InlineRowActionsWebComponent;
    menuBtn.inlineActions = actions;
    root.appendChild(menuBtn);
  } else {
    menuBtn.className = '';
    menuBtn.state = null;
    menuBtn.canEdit = this.isEditableCellActive(rowData);
  }
  menuBtn.itemData = rowData;
  menuBtn.classList.add(`row-${rowData.index + 2}`);
  root.item = rowData.item;
}

export function multipleValueRenderer<Column extends number, ColumnKeys extends string>(
  rowData,
  columnDetails: ColumnDetails<Column, ColumnKeys>
) {
  const cellValue = rowData.item[columnDetails.key];

  if (!Array.isArray(cellValue)) {
    return gridCellDefaultRenderer.call(this, rowData, columnDetails);
  }

  const firstValue = cellValue[0];
  const valueCounter =
    cellValue.length > 1 ? html`<span class="multiple-value__counter">+${cellValue.length - 1}</span>` : '';

  return html`<gz-grid-cell-default-renderer .label="${html`${firstValue}${valueCounter}`}">
  </gz-grid-cell-default-renderer>`;
}
